/**
 * Generic helper for positioning HTML overlays (on the /sign page) on top of the rendered receipt PDF,
 * based on the position of a "marked" node inside react-pdf's internal layout data (receiptLayout, captured
 * in getCurrentReceiptPages() via the onRender callback's _INTERNAL__LAYOUT__DATA_).
 *
 * This is the same technique used by receiptSign.js for the customer signature field, generalized so it can
 * locate ANY marked View/Text anywhere in the tree.
 *
 * To mark a spot in the generated PDF, pass a truthy boolean prop (the "marker") to the View/Text you want to
 * find later, e.g. <View receiptWorkOrderNumberField={true}>...</View>, then call getReceiptFieldStyles() with
 * that same prop name.
 *
 * ---
 * TWO THINGS THAT WERE WRONG HERE (found 2026-08-08, verified by reproducing @react-pdf/renderer's actual
 * _INTERNAL__LAYOUT__DATA_ output in isolation - see notes below):
 *
 * 1) `node.origin.top`/`node.origin.left` is NOT the node's top-left position relative to its parent - it is
 *    the node's CENTER point relative to its parent's content box. (Confirmed empirically: rendering a
 *    single-page A4 doc and logging the PAGE node's own `origin` gives exactly {left: pageWidth/2,
 *    top: pageHeight/2}; a plain full-width child row's origin.left comes out to paddingLeft + rowWidth/2, etc.)
 *    The old `findMarkedNodePosition` summed these raw centers as if they were top-left deltas. For a node 1
 *    level deep (a direct child of the page) that's a small, roughly-constant error - which is exactly the
 *    "adjustPosition" pattern receiptSign.js was tuned around. But summed across the 4-5 levels our new fields
 *    are nested at, the error compounds every level and blows up to hundreds of points past the actual page
 *    edge (measured: a 5-level-deep marker landed around left=915/top=535 in a reproduction where the correct
 *    answer was left=105/top=45 - the same order of magnitude as the left:1200-1400 values logged from the
 *    real app, on an A4 page that's only ~595pt wide). That's why the position comes out "outside of the pdf
 *    page" and why it's still wrong even with adjustPositionValue removed - the input itself was already wrong.
 *
 *    Fix: convert each node's own origin from "center relative to parent" to "top-left relative to parent"
 *    using THAT SAME node's own box width/height (origin.top - box.height/2, origin.left - box.width/2)
 *    before accumulating.
 *
 * 2) Once the position above is correct, it's a true PDF-point coordinate on the page (72pt/inch, (0,0) at the
 *    page's own top-left corner). The on-screen <Page> is rendered by react-pdf's pdf.js-based viewer at
 *    `scale={RECEIPT_PAGE_RENDER_SCALE}` (see contractsPopupDocument.js / contractInterface.js), where 1 PDF
 *    point = 1 * scale CSS *pixel* - not 1 CSS "pt" unit. CSS "pt" is a fixed physical unit (1pt = 1.333px)
 *    that has nothing to do with the page's render scale, so writing `top: "536pt"` silently multiplies by
 *    1.333 instead of by the actual 1.5 render scale, on top of whatever the wrong input already did.
 *    (The old quadratic `adjustPositionValue` was an empirical curve fit that happened to roughly cancel this
 *    out ONLY for one specific shallow node - b=1.123 times the CSS pt->px factor of 1.333 is ~1.497, i.e. it
 *    was accidentally approximating "multiply by the 1.5 render scale" through a roundabout unit trick. It
 *    only worked at that node's shallow depth/small magnitude; it can't fix arbitrary depth or the real bug in (1).)
 *
 *    Fix: multiply the corrected point position by the SAME scale passed to <Page scale={...}/>, and use "px"
 *    as the CSS unit (not "pt").
 * ---
 **/

// each node's own origin.top/origin.left (as reported by @react-pdf/renderer's internal layout data) is that
// node's CENTER point relative to its parent's content box, not its top-left corner - so we subtract half of
// the node's own box dimensions to get a proper top-left-relative-to-parent delta before accumulating it
function getOwnTopLeftDelta(node) {
  const width = node.box ? node.box.width || 0 : 0;
  const height = node.box ? node.box.height || 0 : 0;
  const centerTop = node.origin ? node.origin.top || 0 : 0;
  const centerLeft = node.origin ? node.origin.left || 0 : 0;
  return {
    top: centerTop - height / 2,
    left: centerLeft - width / 2,
  };
}

// walks the tree accumulating each visited ancestor's own top-left-relative-to-parent delta, so the returned
// position for the marked node ends up as a true page-relative (top-left-origin) position, in PDF points
function findMarkedNodePosition(node, markerName, accTop, accLeft) {
  if (!node) {
    return null;
  }
  const delta = getOwnTopLeftDelta(node);
  const top = accTop + delta.top;
  const left = accLeft + delta.left;

  // the marked node's own box.width/height (still in PDF points, unscaled) so callers can size the overlay
  // to match the actual rendered field instead of guessing a width/height
  const width = node.box ? node.box.width || 0 : 0;
  const height = node.box ? node.box.height || 0 : 0;

  if (node.props && node.props[markerName]) {
    return {top, left, width, height};
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findMarkedNodePosition(child, markerName, top, left);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

// scale must match whatever <Page scale={...}/> the receipt is actually rendered with (see
// RECEIPT_PAGE_RENDER_SCALE in contractInterface.js) - the layout data is in PDF points, and the on-screen
// canvas renders 1 point as 1 * scale CSS pixel
export const getReceiptFieldPosition = (receiptLayout, currentPage, markerName, scale) => {
  const page = receiptLayout && receiptLayout.children && receiptLayout.children[currentPage];
  if (!page) {
    return null;
  }
  // start accumulation at the page itself (0,0) - each page's Yoga tree is calculated independently, and
  // getOwnTopLeftDelta() on the page node itself resolves to {top: 0, left: 0} (its origin is exactly its own
  // half-width/half-height, i.e. its own center), so the returned position is relative to the page's top-left
  const position = findMarkedNodePosition(page, markerName, 0, 0);
  if (!position) {
    return null;
  }
  return {
    top: position.top * scale,
    left: position.left * scale,
    width: position.width * scale,
    height: position.height * scale,
  };
}

const HARDCODE_HEIGHT_SHIFT = 26;

// defaultSize (optional) - {width, height} in PDF points (unscaled), same unit as everything in
// printDocumentStyles.js. Pass this ONLY when the marked node's own measured box can't be trusted (e.g. it has
// no explicit width and its Text is rendered as "" during the isContractSign pass, so the real box collapses
// to ~0 - see RECEIPT_WORK_ORDER_FIELD_WIDTH/HEIGHT in printDocumentStyles.js for the one field that needs
// this today). When provided, it always wins over the measured box.
export const getReceiptFieldStyles = (receiptLayout, currentPage, markerName, scale, defaultSize, translateX = 0) => {
  let styles = {
    position: "absolute",
  };
  const position = getReceiptFieldPosition(receiptLayout, currentPage, markerName, scale);

  if (position) {
    // px, not pt - see the note at the top of this file. The values are already-scaled on-screen pixels.
    styles.top = position.top + HARDCODE_HEIGHT_SHIFT + "px";
    // styles.left = position.left + "px";
    styles.left = "50%";
    styles.transform = `translateX(${translateX}pt)`
  }

  const width = defaultSize && defaultSize.width ? defaultSize.width * scale : position && position.width;
  const height = defaultSize && defaultSize.height ? defaultSize.height * scale : position && position.height;
  if (width) {
    styles.width = width + "px";
  }
  if (height) {
    styles.height = height + "px";
  }

  return styles;
}
